// Account & billing (Cesar, Oct 18): subscription, seats, invoices, payment method and cancellation. Invoices, card
// and cancellation open Stripe's customer portal; seats change here. Also the welcome page after Checkout and the
// screen shown instead of the app when the subscription isn't active.
import { useAction, useQuery } from "convex/react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, ExternalLink, Loader2, Users } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { Badge, Button, Card, Input, PageHeader, Spinner, useRun } from "@/components/ui";
import { siteLink } from "@/lib/site";

const day = (ms?: number) => (ms ? new Date(ms).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "");
const STATUS: Record<string, { label: string; tone: "green" | "amber" | "red" | "blue" | "slate" }> = {
  trialing: { label: "Free trial", tone: "blue" },
  active: { label: "Active", tone: "green" },
  past_due: { label: "Payment due", tone: "amber" },
  unpaid: { label: "Unpaid", tone: "red" },
  canceled: { label: "Ended", tone: "red" },
  incomplete: { label: "Not started", tone: "amber" },
  comped: { label: "Complimentary", tone: "green" },
};

type Status = NonNullable<ReturnType<typeof useBillingStatus>>;
function useBillingStatus() {
  return useQuery(api.billing.status);
}

/** Subscribe (or re-subscribe) through Stripe Checkout. */
function SubscribeButton({ s, label = "Subscribe" }: { s: Status; label?: string }) {
  const checkout = useAction(api.billing.startCheckout);
  const run = useRun();
  const [seats, setSeats] = useState(Math.max(s.billing?.seats ?? 1, s.used.total));
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-sm">
        <span className="block text-xs text-slate-600">Users</span>
        <Input type="number" className="w-24" min={Math.max(1, s.used.total)} value={seats} onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} />
      </label>
      <Button
        variant="primary"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          const url = await run(() => checkout({ seats }));
          if (url) window.location.href = url;
          else setBusy(false);
        }}
      >
        <CreditCard className="size-4" /> {label} · ${seats * s.pricePerSeat}/month
      </Button>
    </div>
  );
}

export function BillingPage() {
  const s = useBillingStatus();
  const portal = useAction(api.billing.openPortal);
  const changeSeats = useAction(api.billing.changeSeats);
  const run = useRun();
  const [seats, setSeats] = useState<number | null>(null);
  const [opening, setOpening] = useState(false);
  if (!s) return <Spinner />;
  const b = s.billing;
  const st = b ? STATUS[b.status] : null;
  const canceled = new URLSearchParams(window.location.search).get("checkout") === "canceled";
  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <PageHeader title="Account & billing" sub="Your subscription, users, invoices and payment method." />
      {!s.enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Online billing isn't switched on for this site yet, so there's nothing to pay. Your organisation has full access.
        </div>
      )}
      {canceled && s.enabled && !s.access.ok && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">Checkout was cancelled. You can start it again below whenever you're ready.</div>}
      <Card title="Subscription">
        {s.legacy || !b ? (
          <p className="text-sm text-slate-600">{s.enabled ? "Your organisation joined during the beta and has complimentary access." : "No subscription needed."}</p>
        ) : (
          <div className="grid gap-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs text-slate-500">Plan</p>
              <p className="font-medium">Mongoose EVM, monthly</p>
              <p className="text-slate-600">${s.pricePerSeat} per user / month</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <p>{st && <Badge tone={st.tone}>{st.label}</Badge>}</p>
              {b.status === "trialing" && b.trialEnd && <p className="mt-1 text-slate-600">Trial ends {day(b.trialEnd)}</p>}
              {b.cancelAtPeriodEnd && b.currentPeriodEnd && <p className="mt-1 text-amber-700">Cancels on {day(b.currentPeriodEnd)}</p>}
              {!b.cancelAtPeriodEnd && b.status === "active" && b.currentPeriodEnd && <p className="mt-1 text-slate-600">Renews {day(b.currentPeriodEnd)}</p>}
            </div>
            <div>
              <p className="text-xs text-slate-500">Monthly total</p>
              <p className="font-medium">${(b.seats * s.pricePerSeat).toFixed(2)}</p>
              <p className="text-slate-600">plus applicable taxes</p>
            </div>
          </div>
        )}
        {s.enabled && s.isAdmin && b && b.status !== "comped" && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {b.subscriptionId ? (
              <Button
                busy={opening}
                onClick={async () => {
                  setOpening(true);
                  const url = await run(() => portal({}));
                  if (url) window.location.href = url;
                  else setOpening(false);
                }}
              >
                <ExternalLink className="size-4" /> Invoices, payment method and cancellation
              </Button>
            ) : null}
            {(!b.subscriptionId || b.status === "canceled") && <SubscribeButton s={s} label={b.status === "canceled" ? "Subscribe again" : "Subscribe"} />}
          </div>
        )}
        {!s.isAdmin && s.enabled && <p className="mt-3 text-xs text-slate-500">Only your organisation's admins can change the subscription.</p>}
      </Card>
      <Card title="Users">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="size-4 text-slate-500" />
            <span>
              <b>{s.used.members}</b> member{s.used.members === 1 ? "" : "s"}
              {s.used.invites ? `, ${s.used.invites} open invite${s.used.invites === 1 ? "" : "s"}` : ""}
              {s.seatLimit !== null ? (
                <>
                  {" "}
                  of <b>{s.seatLimit}</b> paid seat{s.seatLimit === 1 ? "" : "s"}
                </>
              ) : (
                " (no seat limit)"
              )}
            </span>
          </div>
          <Link to="/team" className="text-brand-600 underline">
            Manage team
          </Link>
        </div>
        {s.enabled && s.isAdmin && b?.subscriptionId && b.status !== "canceled" && b.status !== "comped" && (
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
            <label className="text-sm">
              <span className="block text-xs text-slate-600">Paid seats</span>
              <Input type="number" className="w-24" min={Math.max(1, s.used.total)} value={seats ?? b.seats} onChange={(e) => setSeats(Number(e.target.value) || 1)} />
            </label>
            <Button disabled={seats === null || seats === b.seats} onClick={() => run(() => changeSeats({ seats: seats! }), "Seats updated").then(() => setSeats(null))}>
              Change seats
            </Button>
            <p className="w-full text-xs text-slate-500">Adding seats is charged pro rata for the rest of this month; removing seats credits the next invoice. Each member and open invite uses a seat.</p>
          </div>
        )}
      </Card>
      <Card title="Help">
        <p className="text-sm text-slate-600">
          Questions about your subscription or invoices?{" "}
          <a className="text-brand-600 underline" href={siteLink("help")} target="_blank" rel="noreferrer">
            Contact support
          </a>
          . Subscription terms:{" "}
          <a className="text-brand-600 underline" href={siteLink("terms")} target="_blank" rel="noreferrer">
            Terms of Service
          </a>
          .
        </p>
      </Card>
    </div>
  );
}

/** Shown instead of the app while the organisation's subscription isn't active. */
export function BillingGate({ reason }: { reason?: string }) {
  const s = useBillingStatus();
  if (!s) return <Spinner />;
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-slate-200 bg-surface p-6 shadow-sm">
        <h1 className="text-lg font-semibold">{s.billing?.status === "incomplete" ? "One more step" : "Subscription needed"}</h1>
        <p className="text-sm text-slate-600">{reason}</p>
        {s.isAdmin ? (
          <>
            {s.billing?.subscriptionId ? (
              <Link to="/billing" className="text-sm text-brand-600 underline">
                Go to Account &amp; billing to update your payment method
              </Link>
            ) : (
              <SubscribeButton s={s} label={s.trialDays ? `Start ${s.trialDays}-day free trial` : "Subscribe"} />
            )}
            <p className="text-xs text-slate-500">
              ${s.pricePerSeat} per user per month. Payment details are entered securely on Stripe.
              {s.trialDays ? ` You won't be charged until the ${s.trialDays}-day trial ends.` : ""}
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-600">Ask your organisation's admin to set up the subscription.</p>
        )}
      </div>
    </div>
  );
}

/** Stripe Checkout returns here: confirm payment, then point the user at the first steps. */
export function WelcomePage() {
  const sync = useAction(api.billing.syncCheckout);
  const s = useBillingStatus();
  const [synced, setSynced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("session_id");
    if (!id) return setSynced(true);
    sync({ sessionId: id })
      .catch((e) => setError(String((e as Error).message ?? e)))
      .finally(() => setSynced(true));
  }, [sync]);
  const ok = s?.access.ok && s.billing && s.billing.status !== "incomplete";
  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <div className="rounded-lg border border-slate-200 bg-surface p-6 shadow-sm">
        {!s || (!synced && !ok) ? (
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="size-4 animate-spin" /> Confirming your payment with Stripe...
          </p>
        ) : ok ? (
          <>
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              <CheckCircle2 className="size-6 text-emerald-600" /> You're all set
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {s.billing!.status === "trialing" && s.billing!.trialEnd ? `Your free trial runs until ${day(s.billing!.trialEnd)}. ` : "Your subscription is active. "}
              {s.billing!.seats} user{s.billing!.seats === 1 ? "" : "s"}. A receipt is on its way from Stripe.
            </p>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
              Open Mongoose
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Payment not confirmed yet</h1>
            <p className="mt-2 text-sm text-slate-600">{error ?? "Stripe hasn't confirmed the payment yet. This page updates by itself; if it doesn't within a minute, check Account & billing or contact support."}</p>
            <Link to="/billing" className="mt-3 inline-block text-sm text-brand-600 underline">
              Account &amp; billing
            </Link>
          </>
        )}
      </div>
      <Card title="Getting started">
        <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-700">
          <li>
            <b>Library:</b> review the milestones, milestone chains and document types every project uses (a default set is loaded for you).
          </li>
          <li>
            <b>New project:</b> in Projects, create a project, then set up its breakdowns (WBS, DBS...) and calendar of reporting periods.
          </li>
          <li>
            <b>Deliverables:</b> add them one by one, from the reference library, or import your list from Excel.
          </li>
          <li>
            <b>Budget:</b> enter the main budget and lock the baseline when it matches the deliverables.
          </li>
          <li>
            <b>Each period:</b> enter earned dates in Progress update and actual hours, then close the period and print the reports.
          </li>
          <li>
            <b>Team:</b> invite your colleagues as admins, project managers or viewers.
          </li>
        </ol>
        <p className="mt-3 text-sm text-slate-600">
          Full quick-start guide:{" "}
          <a className="text-brand-600 underline" href={siteLink("help")} target="_blank" rel="noreferrer">
            Help
          </a>
        </p>
      </Card>
    </div>
  );
}
